// Code2Commerce global behaviors: loader fade, theme toggle, magnetic
// buttons, scroll reveals, custom cursor. Honors prefers-reduced-motion
// and the merchant's data-motion="reduced" override.

(function () {
  'use strict';

  var THEME_STORAGE_KEY = 'c2c-theme';
  var LIGHT_CLASS = 'color-scheme-2';
  var html = document.documentElement;

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionReduced = prefersReduced || html.dataset.motion === 'reduced';

  // --- Theme toggle -------------------------------------------------------
  // The pre-paint script in <head> already applies the saved theme to <html>
  // before first render to avoid FOUC. Here we wire the toggle button and
  // keep multiple tabs in sync.
  function applyTheme(value) {
    if (value === 'light') {
      html.classList.add(LIGHT_CLASS);
    } else {
      html.classList.remove(LIGHT_CLASS);
    }
  }

  function toggleTheme() {
    var isLight = html.classList.contains(LIGHT_CLASS);
    var next = isLight ? 'dark' : 'light';
    applyTheme(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (e) {
      // localStorage blocked (private mode); just apply for the session.
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-c2c-theme-toggle]');
    if (btn) {
      e.preventDefault();
      toggleTheme();
    }
  });

  window.addEventListener('storage', function (e) {
    if (e.key === THEME_STORAGE_KEY) {
      applyTheme(e.newValue);
    }
  });

  // --- Loader -------------------------------------------------------------
  // Mirrors design-reference/assets/shared.js initLoader: rAF-driven progress
  // counter with ease-out cubic over 900ms, 150ms hold at 100%, then fade.
  // Hard fallback at 1800ms in case rAF is paused (background tab). Adds
  // `body.loaded` class on finish so hero animations can chain off it.
  function initLoader() {
    var loader = document.querySelector('[data-c2c-loader]');
    var pct = loader && loader.querySelector('[data-c2c-loader-pct]');
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      if (pct) pct.textContent = '100';
      if (loader) {
        loader.classList.add('is-done');
        // Remove the node after the CSS transition so it doesn't trap clicks.
        setTimeout(function () {
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        }, 900);
      }
      document.body.classList.add('loaded');
    }

    if (!loader) {
      // Loader is disabled via theme settings; still flag body.loaded so
      // sections that key animations off it ("body.loaded .hero-title…") fire.
      document.body.classList.add('loaded');
      return;
    }

    if (motionReduced) {
      finish();
      return;
    }

    var DURATION = 900;
    var start = performance.now();
    function frame(now) {
      if (finished) return;
      var t = Math.min(1, (now - start) / DURATION);
      var eased = 1 - Math.pow(1 - t, 2);
      var displayed = Math.floor(eased * 100);
      if (pct) pct.textContent = String(displayed).padStart(3, '0');
      if (t >= 1) {
        setTimeout(finish, 150);
      } else {
        requestAnimationFrame(frame);
      }
    }
    requestAnimationFrame(frame);

    // Hard fallback: always dismiss within 1.8s even if rAF is paused.
    setTimeout(finish, 1800);
    // Also dismiss shortly after window.load if that happens first.
    window.addEventListener(
      'load',
      function () {
        setTimeout(finish, 400);
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoader, { once: true });
  } else {
    initLoader();
  }

  // --- Magnetic buttons ---------------------------------------------------
  // Slight cursor-toward-element pull. Disabled under reduced motion and on
  // pointer:coarse devices.
  function initMagnetic() {
    if (motionReduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.c2c-magnetic').forEach(function (el) {
      var strength = parseFloat(el.dataset.magnetic) || 0.2;
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var dx = (e.clientX - (rect.left + rect.width / 2)) * strength;
        var dy = (e.clientY - (rect.top + rect.height / 2)) * strength;
        el.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }

  // --- Scroll reveals -----------------------------------------------------
  function initReveals() {
    var nodes = document.querySelectorAll('.c2c-reveal');
    if (!nodes.length) return;
    if (motionReduced || !('IntersectionObserver' in window)) {
      // No animation: just mark everything visible immediately.
      nodes.forEach(function (n) {
        n.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    nodes.forEach(function (n) {
      io.observe(n);
    });
  }

  // --- Custom cursor ------------------------------------------------------
  function initCursor() {
    var dot = document.querySelector('[data-c2c-cursor-dot]');
    var ring = document.querySelector('[data-c2c-cursor-ring]');
    if (!dot || !ring) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var x = window.innerWidth / 2;
    var y = window.innerHeight / 2;
    var ringX = x;
    var ringY = y;
    var revealed = false;

    function loop() {
      // Dot follows mouse instantly; ring lerps for a soft trail.
      ringX += (x - ringX) * 0.18;
      ringY += (y - ringY) * 0.18;
      dot.style.transform = 'translate(' + (x - 4) + 'px, ' + (y - 4) + 'px)';
      ring.style.transform = 'translate(' + (ringX - 18) + 'px, ' + (ringY - 18) + 'px)';
      requestAnimationFrame(loop);
    }
    // Start the loop immediately so the ring lerps toward viewport center
    // rather than sitting at top-left until first mousemove. The elements
    // stay invisible (opacity: 0 default in c2c-cursor.liquid) until the
    // first real mousemove flips .is-ready.
    requestAnimationFrame(loop);

    document.addEventListener(
      'mousemove',
      function (e) {
        x = e.clientX;
        y = e.clientY;
        if (!revealed) {
          revealed = true;
          dot.classList.add('is-ready');
          ring.classList.add('is-ready');
        }
      },
      { passive: true }
    );

    var hoverSelector = 'a, button, [role="button"], .c2c-magnetic, [data-c2c-cursor-hover]';
    document.addEventListener(
      'mouseover',
      function (e) {
        if (e.target.closest && e.target.closest(hoverSelector)) {
          dot.classList.add('is-hover');
          ring.classList.add('is-hover');
        }
      },
      { passive: true }
    );
    document.addEventListener(
      'mouseout',
      function (e) {
        if (e.target.closest && e.target.closest(hoverSelector)) {
          dot.classList.remove('is-hover');
          ring.classList.remove('is-hover');
        }
      },
      { passive: true }
    );
  }

  // --- Spotlight: cursor-follow glow on .has-spotlight cards --------------
  // Sets --mx and --my CSS custom properties on each card relative to its
  // own bounding box, so the ::before radial gradient in c2c-base.css can
  // track the cursor. No-op on touch devices (no useful cursor position).
  function initSpotlight() {
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
    var nodes = document.querySelectorAll('.has-spotlight');
    nodes.forEach(function (el) {
      if (el.dataset.c2cSpotlightBound === '1') return;
      el.dataset.c2cSpotlightBound = '1';
      el.addEventListener(
        'pointermove',
        function (e) {
          var r = el.getBoundingClientRect();
          el.style.setProperty('--mx', e.clientX - r.left + 'px');
          el.style.setProperty('--my', e.clientY - r.top + 'px');
        },
        { passive: true }
      );
    });
  }

  // --- Scroll-aware sticky header ----------------------------------------
  // Hide the floating header when scrolling DOWN past a threshold; show it
  // again on scroll UP or near the top. rAF-throttled. Under reduced motion
  // the header stays permanently visible (no slide).
  function initStickyHeader() {
    var header = document.querySelector('.c2c-header');
    if (!header) return;
    if (motionReduced) return; // always visible; no hide-on-scroll

    var THRESHOLD = 80; // px from top before hiding kicks in
    var DELTA = 4; // ignore sub-pixel jitter
    var lastY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var ticking = false;

    function update() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (y <= THRESHOLD) {
        header.classList.remove('is-hidden');
      } else if (y > lastY + DELTA) {
        header.classList.add('is-hidden'); // scrolling down
      } else if (y < lastY - DELTA) {
        header.classList.remove('is-hidden'); // scrolling up
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
  }

  // --- Init ---------------------------------------------------------------
  function init() {
    initMagnetic();
    initReveals();
    initCursor();
    initSpotlight();
    initStickyHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
